/**
 * SignInIntro: the branded intro every web app shows while it starts (Jean, 30 Oct 2026), after
 * the ROBIS sign-in page: the product's wordmark drawn letter by letter, its subtitle, a loading
 * line, then a fade into the app (signed in) or into the themed sign-in page (signed out).
 *
 * - It stays at least INTRO_FULL_MS the first time in a browser session, INTRO_SHORT_MS after
 *   (the wordmark is then already drawn), so nothing flashes even when auth answers at once.
 * - The minimum runs on one clock from page load (`introHold`), so start-up code can wait for it
 *   before a full-page redirect (keycloak-js check-sso / login-required) that would cut it off.
 * - Signed out: the sign-in screen asks for the redirect (`requestSignIn`), the intro fades its
 *   content out on the dark background and only then answers `signInReady`.
 * - prefers-reduced-motion: no drawing, no fades; the same minimum.
 *
 * JSX-free like SignInScreen (some app builds load shared/ through the dependencies Babel rule).
 *
 * <SignInIntroGate product="ops" subtitle="…" ready={bool} enabled={bool}>{app}</SignInIntroGate>
 *   ready     the app knows where to go (auth resolved); or report it from inside with
 *             <SignInIntroReady when={bool} /> when the gate sits above the auth provider.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MARK_24K, ARKIS_WORDMARK, SJ_SQUARE } from './signInMarks';
import { copyrightLine } from './SignInScreen';
import './signInIntro.css';

export const INTRO_FULL_MS = 2600;
export const INTRO_SHORT_MS = 900;
export const INTRO_FADE_MS = 600;
const SEEN_KEY = '24k-intro-seen';

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());
const BOOT_AT = now();
const modes = {};

/** 'full' the first time this product starts in the browser session, 'short' after. */
export function introMode(product = 'ops', store) {
  if (modes[product]) return modes[product];
  let seen = false;
  try {
    const s = store || window.sessionStorage;
    seen = String(s.getItem(SEEN_KEY) || '').split(',').includes(product);
    if (!seen) s.setItem(SEEN_KEY, [s.getItem(SEEN_KEY), product].filter(Boolean).join(','));
  } catch (_) { /* no storage: always the full intro */ }
  modes[product] = seen ? 'short' : 'full';
  return modes[product];
}

export function introMinMs(mode) {
  return mode === 'short' ? INTRO_SHORT_MS : INTRO_FULL_MS;
}

/** Milliseconds left before the intro may leave, from page load. */
export function introRemaining(product = 'ops', at = now()) {
  return Math.max(0, BOOT_AT + introMinMs(introMode(product)) - at);
}

/** Resolves when the intro's minimum is over: wait for it before a full-page redirect. */
export function introHold(product = 'ops') {
  return new Promise((resolve) => setTimeout(resolve, introRemaining(product)));
}

/** True when the URL carries an identity-server answer (keycloak-js reads it without a redirect). */
export function hasAuthResponse(loc = typeof window !== 'undefined' ? window.location : null) {
  if (!loc) return false;
  return /(?:^|[#&?])(?:state|code|error|session_state)=/.test(`${loc.search || ''}${loc.hash || ''}`);
}

function reducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; }
}

// The approved wordmarks as stroke paths, one entry per letter, so each can be drawn in turn
// (Figma ItvCEMR674lisMxqEtiXv8: "Ops Centre / Wordmark / Approved · spaced" with the orbital O,
// "Infinity / Wordmark / Spaced"). Masks keep the O's orbit gaps and trim the R's leg.
const OPS = {
  name: 'OPS CENTRE',
  width: 1728,
  masks: [
    ['M109.6 0.9H-10.4V53.9H109.6V0.9Z M109.6 67.9H-10.4V120.9H109.6V67.9Z'],
    ['M1613.4 -19.1H1413.4V110.9H1613.4V-19.1Z'],
  ],
  letters: [
    { mask: 0, d: ['M49.6 104.1C73.46 104.1 92.8 84.76 92.8 60.9C92.8 37.04 73.46 17.7 49.6 17.7C25.74 17.7 6.4 37.04 6.4 60.9C6.4 84.76 25.74 104.1 49.6 104.1Z'] },
    { d: ['M206.4 17.7H248.6C269.1 17.7 281.8 26.7 281.8 40.4C281.8 54.1 269.1 63.1 248.6 63.1H206.4V104.1'] },
    { d: ['M473.6 17.7H417C405.07 17.7 395.4 27.37 395.4 39.3C395.4 51.23 405.07 60.9 417 60.9H454.2C466.13 60.9 475.8 70.57 475.8 82.5C475.8 94.43 466.13 104.1 454.2 104.1H397.6'] },
    { d: ['M765.69 33.13C759.99 26.34 752.34 21.47 743.78 19.17C735.22 16.88 726.16 17.27 717.83 20.31C709.49 23.34 702.3 28.86 697.21 36.12C692.13 43.38 689.4 52.03 689.4 60.9C689.4 69.77 692.13 78.42 697.21 85.68C702.3 92.94 709.49 98.46 717.83 101.5C726.16 104.53 735.22 104.92 743.78 102.63C752.34 100.33 759.99 95.46 765.69 88.67'] },
    { d: ['M879.4 17.7H953.4', 'M879.4 60.9H941.4', 'M879.4 104.1H953.4'] },
    { d: ['M1067 71.9V17.7L1145 104.1V17.7'] },
    { d: ['M1258.6 17.7H1336.6', 'M1297.6 67.7V104.1'] },
    { mask: 1, d: ['M1450.2 17.7H1491.4C1512.4 17.7 1526.2 26.4 1526.2 39.4C1526.2 52.9 1512.4 60.9 1491.4 60.9H1477.4L1563.4 127.48'] },
    { d: ['M1646.8 17.7H1720.8', 'M1646.8 60.9H1708.8', 'M1646.8 104.1H1720.8'] },
  ],
};
const INFINITY = {
  name: 'INFINITY',
  width: 1200,
  masks: [],
  letters: [
    { d: ['M6.8 17.8V104.2'] },
    { d: ['M120.4 72V17.8L198.4 104.2V17.8'] },
    { d: ['M312 17.8H386', 'M374 61H312V104.2'] },
    { d: ['M499.6 17.8V104.2'] },
    { d: ['M613.2 72V17.8L691.2 104.2V17.8'] },
    { d: ['M804.8 17.8V104.2'] },
    { d: ['M918.4 17.8H996.4', 'M957.4 67.8V104.2'] },
    { d: ['M1110 17.8L1151.5 63V104.2', 'M1193 17.8L1171.2 41'] },
  ],
};
export const INTRO_WORDMARKS = { ops: OPS, infinity: INFINITY };

const STEP_S = 0.13;
const h = React.createElement;

/** The wordmark drawn letter by letter, its subtitle as wide as the name (as on ROBIS). */
export function IntroWordmark({ product = 'ops', subtitle, draw = true }) {
  const mark = INTRO_WORDMARKS[product] || OPS;
  const subtitleAt = 0.15 + mark.letters.length * STEP_S + 0.2;
  return h('svg', { className: `k24-intro-mark${draw ? ' is-drawing' : ''}`, viewBox: `-8 -8 ${mark.width + 16} ${subtitle ? 214 : 138}`, role: 'img', 'aria-label': subtitle ? `${mark.name}, ${subtitle}` : mark.name, fill: 'none' },
    h('defs', null,
      mark.masks.map((d, i) => h('mask', { key: i, id: `k24-intro-${product}-m${i}`, maskUnits: 'userSpaceOnUse', x: -20, y: -30, width: mark.width + 40, height: 190 },
        h('path', { d, fill: '#fff' })))),
    mark.letters.map((letter, i) => h('g', {
      key: i,
      mask: letter.mask === undefined ? undefined : `url(#k24-intro-${product}-m${letter.mask})`,
      style: { '--k24-delay': `${(0.15 + i * STEP_S).toFixed(2)}s` },
    }, letter.d.map((d, j) => h('path', { key: j, d, pathLength: 1, stroke: '#f2f6fb', strokeWidth: 13.6, strokeLinecap: 'square', strokeMiterlimit: 5 })))),
    subtitle ? h('text', { className: 'k24-intro-subtitle', x: 0, y: 196, textLength: mark.width, lengthAdjust: 'spacing', style: { '--k24-delay': `${subtitleAt.toFixed(2)}s` } }, subtitle) : null);
}

export const SignInIntroContext = createContext(null);
/** The surrounding intro, or null when there is none (the caller then goes on at once). */
export function useSignInIntro() { return useContext(SignInIntroContext); }

/** Reports from inside the auth provider that the app knows where to go. */
export function SignInIntroReady({ when }) {
  const intro = useSignInIntro();
  const setReady = intro && intro.setReady;
  useEffect(() => { if (setReady) setReady(Boolean(when)); }, [setReady, when]);
  return null;
}

export function SignInIntroGate({ product = 'ops', subtitle, ready: readyProp, enabled = true, children }) {
  const [mode] = useState(() => (enabled ? introMode(product) : 'short'));
  const [phase, setPhase] = useState(enabled ? 'intro' : 'done'); // intro → out (→ done) | to-signin → signin
  const [minOver, setMinOver] = useState(!enabled);
  const [readyMarker, setReady] = useState(false);
  const ready = readyProp === undefined ? readyMarker : Boolean(readyProp);
  const wantsSignIn = useRef(false);
  const waiters = useRef([]);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const fadeMs = reducedMotion() ? 0 : INTRO_FADE_MS;

  useEffect(() => {
    if (!enabled) return undefined;
    const t = setTimeout(() => setMinOver(true), introRemaining(product));
    return () => clearTimeout(t);
  }, [enabled, product]);

  // Leave once the minimum is over and the app knows where to go. The short pause lets a
  // sign-in screen that mounted in the same render ask for the redirect first.
  useEffect(() => {
    if (phase !== 'intro' || !minOver || !ready) return undefined;
    const t = setTimeout(() => setPhase(wantsSignIn.current ? 'to-signin' : 'out'), 150);
    return () => clearTimeout(t);
  }, [phase, minOver, ready]);

  useEffect(() => {
    if (phase === 'out') {
      const t = setTimeout(() => setPhase('done'), fadeMs);
      return () => clearTimeout(t);
    }
    if (phase === 'to-signin') {
      const t = setTimeout(() => setPhase('signin'), fadeMs);
      return () => clearTimeout(t);
    }
    if (phase === 'signin') {
      waiters.current.splice(0).forEach((resolve) => resolve());
    }
    return undefined;
  }, [phase, fadeMs]);

  const requestSignIn = useCallback((on = true) => {
    wantsSignIn.current = on;
    // A sign-in that gave up (error, loop guard) shows its own screen: uncover it.
    if (!on && (phaseRef.current === 'signin' || phaseRef.current === 'to-signin')) setPhase('out');
  }, []);
  /** Fades the intro's content out and resolves: call it right before a full-page redirect. */
  const leaveForRedirect = useCallback(() => new Promise((resolve) => {
    wantsSignIn.current = true;
    if (phaseRef.current === 'signin' || phaseRef.current === 'done') { resolve(); return; }
    waiters.current.push(resolve);
    setReady(true);
  }), []);

  const value = useMemo(() => ({
    active: phase !== 'done', signInReady: phase === 'signin', requestSignIn, leaveForRedirect, setReady,
  }), [phase, requestSignIn, leaveForRedirect]);

  const overlay = phase === 'done' ? null : h('div', {
    className: `k24-intro k24-intro--${product === 'infinity' ? 'infinity' : 'ops'} is-${phase} is-${mode}`,
    role: 'status',
    'aria-live': 'polite',
    'aria-label': phase === 'to-signin' || phase === 'signin' ? 'Opening the sign-in page' : 'Loading',
    style: { '--k24-fade': `${fadeMs}ms` },
  },
    h('img', { className: 'k24-intro-sj', src: SJ_SQUARE, alt: '' }),
    h('div', { className: 'k24-intro-center' },
      h(IntroWordmark, { product, subtitle, draw: mode === 'full' }),
      h('div', { className: 'k24-intro-loading', 'aria-hidden': true }, h('span'))),
    h('footer', { className: 'k24-intro-foot' },
      h('span', { className: 'k24-intro-powered' },
        'Powered by',
        h('img', { className: 'k24-intro-24k', src: MARK_24K, alt: '24K' }),
        h('span', { 'aria-hidden': true }, '·'),
        h('span', { className: 'k24-intro-lockup' },
          h('img', { className: 'k24-intro-arkis', src: ARKIS_WORDMARK, alt: 'ARKIS' }),
          h('span', { className: 'k24-intro-engine' }, 'ENGINE'))),
      h('span', null, copyrightLine())));

  return h(SignInIntroContext.Provider, { value }, children, overlay);
}
